<template>
  <div v-if="liquid.enableV2" class="size-finder size-finder__v2 critical-component-clear">
    <div class="size-finder__header">
      <h3
        class="size-finder__title text-heading-l"
        v-html="liquid.title.replace('Recommended Sizing', 'Size Guide')"
      />

      <p
        v-if="productTitle"
        class="size-finder__product-title text-body-s-bold"
        v-text="productTitle"
      />

      <button
        class="size-finder__close icon-button"
        type="button"
        @click="closeSizeFinderOverlay"
      >
        <icon-close />
      </button>
    </div>

    <div class="size-finder__body">
      <p
        v-if="tabs[0].additionalTableInfo"
        class="size-finder__sub-heading text-body-m-bold text-heading-l-desktop"
        v-text="tabs[0].additionalTableInfo"
      />

      <responsive-image
        v-if="liquid.mainAreaImage"
        :image="liquid.mainAreaImage"
        class="size-finder__main-area-image"
        :alt="liquid.mainAreaImage.alt || 'Size guide measurement diagram'"
      />

      <size-guide-tab
        :tab-content="tabs[0]"
        :enable-unit-toggle="liquid.enableUnitToggle"
        :switch-to-inches="true"
        :switch-to-coat="false"
      />

      <template
        v-for="(tab, index) of tabs"
        :key="index"
      >
        <div
          v-if="tab.enableTab"
          class="accordion__item"
          :class="[
            $blockClass(blockClass, 'accordion'),
          ]"
        >
          <button
            type="button"
            class="accordion__trigger"
            :class="[
              $blockClass(blockClass, 'trigger'),
              { [$class('active')]: isActive(index) }
            ]"
            :aria-expanded="isActive(index)"
            @click="toggleAccordion(index)"
          >
            <span
              class="accordion__trigger-label"
              :class="[
                $blockClass(blockClass, 'trigger-label'),
                { [$class('active')]: isActive(index) }
              ]"
              v-text="tab.tabTitle"
            />

            <icon-plus />
          </button>

          <transition-height>
            <div
              v-show="isActive(index)"
              class="accordion__body"
              :class="[
                $blockClass(blockClass, 'body'),
              ]"
              :ref="'accordionBody' + index"
            >
              <size-guide-tab
                v-if="index === 0"
                :tab-content="tabs[index]"
                :enable-unit-toggle="liquid.enableUnitToggle"
              />

              <fit-guide-tab
                v-if="index === 1"
                :tab-content="tabs[index]"
              />

              <garment-measurements-tab
                v-if="index === 2"
                :tab-content="tabs[index]"
                :enable-unit-toggle="liquid.enableUnitToggle"
              />
            </div>
          </transition-height>
        </div>
      </template>
    </div>
  </div>

  <div v-else class="size-finder size-finder__v1 critical-component-clear">
    <div class="size-finder__header">
      <h3
        class="size-finder__title text-heading-l"
        v-html="liquid.title"
      />

      <button
        class="size-finder__close icon-button"
        type="button"
        @click="closeSizeFinderOverlay"
      >
        <icon-close />
      </button>

      <nav
        class="size-finder__navigation"
        :class="{[$class('hidden')]: enabledTabs.length === 1 && liquid.hideNavigation}"
        :data-test="enabledTabs.length"
        :data-nav="liquid.hideNavigation"
        role="navigation"
      >
        <ul
          class="size-finder__navigation-list"
          :class="{'size-finder__navigation-list--grid': enabledTabs.length === 2}"
          :style="gridColumns()"
        >
          <li
            v-for="(item, index) of enabledTabs"
            :key="index"
            class="size-finder__navigation-item"
          >
            <button
              class="size-finder__navigation-link"
              :class="{ [$class('active')]: index === activeTab }"
              type="button"
              @click="handleTabClick(index, item.tabTitle)"
              v-text="item.tabTitle"
            />
          </li>
        </ul>
      </nav>
    </div>

    <div class="size-finder__body">
      <size-guide-tab
        v-if="tabs[0].tabTitle === activeTabTitle"
        :tab-content="tabs[0]"
        :enable-unit-toggle="liquid.enableUnitToggle"
      />

      <fit-guide-tab
        v-if="tabs[1].tabTitle === activeTabTitle"
        :tab-content="tabs[1]"
      />

      <garment-measurements-tab
        v-if="tabs[2].tabTitle === activeTabTitle"
        :tab-content="tabs[2]"
        :enable-unit-toggle="liquid.enableUnitToggle"
      />
    </div>

    <div
      v-if="liquid.footer"
      class="size-finder__footer"
      v-html="liquid.footer"
    />
  </div>
</template>

<script>

/**
 * Vue: Size finder (size-finder)
 * -----------------------------------------------------------------------------
 * Component to display product size guide.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
import { mapActions } from 'vuex'
import IconClose from '~icons/directional-navigation/close.svg'
import ResponsiveImage from '~global/images/responsive-image'

import SizeGuideTab from './size-guide-tab'
import FitGuideTab from './fit-guide-tab'
import GarmentMeasurementsTab from './garment-measurements-tab'

import IconPlus from '~icons/directional-navigation/plus.svg'
import TransitionHeight from '~async/transition-height/transition-height'

export default {
  name: 'SizeFinder',

  components: {
    IconClose,
    SizeGuideTab,
    FitGuideTab,
    GarmentMeasurementsTab,
    TransitionHeight,
    IconPlus,
    ResponsiveImage,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        title: '',
        footer: '',
        hideNavigation: false,
        enableUnitToggle: false,
        enableV2: false,
      }),
    },
    tabs: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      activeTab: 0,
      activeTabTitle: '',
      accordionData: [],
      accordions: [],
    }
  },

  computed: {

    /**
     * Returns enabled tabs.
     * @returns {Object}
     */
    enabledTabs() {
      return this.tabs.filter((tab) => tab.enableTab)
    },

    /**
     * Returns Main Product title.
     */
    productTitle() {
      const productTitle = document.querySelector('.main-product__title').innerHTML

      return productTitle ? productTitle : false
    }
  },

  mounted() {
    this.activeTabTitle = this.enabledTabs[0].tabTitle
    this.activeTab = 0

    this.structureAccordionData()
    this.accordions = this.accordionData.map((accordion) => {
      return {
        active: false,
        ...accordion,
      }
    })
  },

  methods: {

    /**
     * Creates accordion structured data.
     */
    structureAccordionData() {
      for (const tab of this.tabs) {
        this.accordionData.push({
          title: tab.tabTitle,
          body: tab.firstParagraphContent ? tab.firstParagraphContent : tab.paragraphContent
        })
      }
    },

    /**
     * Check if an accordion is active using its index.
     * @param {Number} index - Index of a specific accordion.
     * @returns {Boolean}
     */
     isActive(index) {
      return this.accordions[index]?.active
    },

    /**
     * Toggles the clicked accordion
     * @param {String} accordionIndex - Index of the clicked accordion
     */
     toggleAccordion(accordionIndex) {

      if (this.isActive(accordionIndex)) {
        this.accordions[accordionIndex].active = false
        return
      }
      this.accordions.forEach((panel, index) => {
        panel.active = accordionIndex === index
      })

      // Scroll the opened accordion into view after transition
      this.$nextTick(() => {
        setTimeout(() => {
          const refName = 'accordionBody' + accordionIndex
          let accordionBody = this.$refs[refName]
          // If ref is an array (as with v-for), get the first element
          if (Array.isArray(accordionBody)) {
            accordionBody = accordionBody[0]
          }
          if (accordionBody && accordionBody.scrollIntoView) {
            accordionBody.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }
        }, 300)
      })
    },

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Return css style for enabled tabs.
     * @returns {String}
     */
    gridColumns() {
      return `grid-template-columns: repeat(${this.enabledTabs.length}, 1fr)`
    },

    /**
     * Open size finder overlay.
     */
    openSizeFinderOverlay() {
      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'sizeFinderModal',
      })
    },

    /**
     * Close size finder overlay.
     */
    closeSizeFinderOverlay() {
      this.closeOverlay('sizeFinderModal')
    },

    /**
     * Handle Tab click.
     * @param {String} index - Tab index.
     * @param {String} name - Tab title.
     */
    handleTabClick(index, name) {
      this.activeTab = index
      this.activeTabTitle = name
    },
  },
}
</script>

<style lang="scss">
@import './size-finder';
</style>
